import { useState } from "react";
import { useLocation } from "react-router-dom";
import FormField from "components/v2/Auth/AuthField";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useSendPasswordResetOtp from "api/UserResource/useSendPasswordResetOtp";
import useResetPassword from "api/UserResource/useResetPassword";
import AuthLink from "components/v2/Auth/AuthLink";

const ResetPasswordForm = () => {
	const location = useLocation();
	const presetEmail =
		new URLSearchParams(location.search).get("email")
		|| new URLSearchParams(location.search).get("resetEmail")
		|| "";
	const [stage, setStage] = useState("email");
	const [sentTo, setSentTo] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const { mutateAsync: resetPassword, isLoading: isResetting } = useResetPassword();
	const { mutateAsync: sendOtp, isLoading: isSending } = useSendPasswordResetOtp();
	const busy = isSending || isResetting;
	const codeSent = stage === "code";
	const validate = Yup.object().shape({
		username: Yup.string().required("Enter your email address").email("Enter a valid email address"),
		otp: codeSent ? Yup.string().required("Enter the code from your email") : Yup.string().notRequired(),
		password: codeSent ? Yup.string().required("Choose a new password").min(6, "Use at least 6 characters")
			.matches(/[A-Z]/, "Include an uppercase letter").matches(/[a-z]/, "Include a lowercase letter")
			.matches(/[0-9]/, "Include a number") : Yup.string().notRequired(),
	});

	const handleSubmit = async (data) => {
		setErrorMessage("");
		try {
			if (!codeSent) {
				await sendOtp(data);
				setSentTo(data.username);
				setStage("code");
			} else {
				await resetPassword({ ...data, password_confirmation: data.password });
				setStage("done");
			}
		} catch (error) {
			setErrorMessage(error?.response?.data?.message || "Unable to continue. Please try again.");
		}
	};

	if (stage === "done") return (
		<div className="auth-recovery">
			<div className="auth-recovery__notice" role="status"><FiCheckCircle size={24} className="text-akgreenest mb-2" aria-hidden="true" /><strong>Your password is updated.</strong><p>You can now log in with your new password.</p></div>
			<AuthLink className="auth-primary">Back to login <FiArrowRight size={18} aria-hidden="true" /></AuthLink>
		</div>
	);

	return (
		<Formik initialValues={{ username: presetEmail, otp: "", password: "" }} validationSchema={validate} onSubmit={handleSubmit}>
			<Form className="auth-recovery" noValidate>
				{codeSent && <div className="auth-recovery__notice" role="status">We sent a code to <strong>{sentTo}</strong>. Check your inbox, then enter it below.</div>}
				<FormField name="username" type="email" label="Email address*" placeholder="you@example.com" readOnly={codeSent} />
				{codeSent && <>
					<FormField name="otp" label="Verification code*" placeholder="Enter your code" />
					<FormField name="password" type="password" label="New password*" helperText="6+ characters, with uppercase and lowercase letters and a number." />
					<button type="button" className="auth-recovery__resend" disabled={busy} onClick={() => { setStage("email"); setErrorMessage(""); }}>Didn't receive a code? Try again</button>
				</>}
				{errorMessage && <p className="auth-form-error" role="alert">{errorMessage}</p>}
				<button type="submit" className="auth-primary" disabled={busy} aria-busy={busy}>
					{busy ? (isSending ? "Sending your code…" : "Saving your password…") : (codeSent ? "Save new password" : "Send verification code")}
					{busy ? <AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" /> : <FiArrowRight size={18} aria-hidden="true" />}
				</button>
			</Form>
		</Formik>
	);
};

export default ResetPasswordForm;
